import { ComputedRef, Ref, computed, getCurrentInstance, ref } from 'vue';
import { AlertType } from '@/interfaces/IAlert';
import { IOrder } from '@/interfaces/IOrder';
import {
  ITableDiscountState,
  deriveTableDiscountState,
} from '@/domain/pos/table-discount-derivation';
import { deriveOpenTableSession } from '@/domain/pos/table-session-derivation';
import DiscountsService from '@/api/services/discounts.service';
import PosV2TableDiscountModal, {
  ITableDiscountModalResult,
} from '@/components/pos-v2/modals/PosV2TableDiscountModal.vue';
import { useModal } from './use-modal';
import { useUi } from './use-ui';

/**
 * Table-level discount flow for the POS V2 table footer: derives the chip
 * state from the table's pending orders, opens the modal and issues the
 * PUT/DELETE calls. No optimistic store writes — the WS `v2:orderUpdate`
 * burst refreshes the chip reactively. Must be called from a component
 * setup context (uses the proxy for i18n).
 */
export function useTableDiscount(
  tableOrders: Ref<IOrder[]>,
  tableName: Ref<string | null>
) {
  const modals = useModal();
  const ui = useUi();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const openSession = computed(() =>
    deriveOpenTableSession(tableOrders.value)
  );

  const state: ComputedRef<ITableDiscountState> = computed(() =>
    deriveTableDiscountState(tableOrders.value, openSession.value?.id ?? null)
  );

  const saving = ref(false);

  async function openModal(): Promise<void> {
    const session = openSession.value;
    if (!session || saving.value) return;

    const currentState = state.value;
    const result = await modals.showDynamicDialog<ITableDiscountModalResult | null>(
      PosV2TableDiscountModal,
      {
        tableName: tableName.value,
        tableOrders: tableOrders.value,
        openSessionId: session.id,
        initial: currentState.active,
        hasPosOwnedOrders: currentState.hasPosOwnedOrders,
        paidMoneyPresent: currentState.paidMoneyPresent,
        uncoveredEligibleCount: currentState.uncoveredEligibleCount,
      },
      'pos-table-discount',
      { width: 480 }
    );
    if (!result) return;

    if (result.action === 'remove') {
      const confirmed = await modals.confirm({
        title: i18nT('posV2.tableDiscountRemove'),
        message: i18nT('posV2.tableDiscountRemoveConfirm'),
      });
      if (!confirmed) return;
    }

    saving.value = true;
    try {
      if (result.action === 'apply') {
        await DiscountsService.setTableSessionDiscount(session.id, {
          isPercentage: result.isPercentage,
          value: result.value,
        });
        ui.showToast(
          AlertType.Success,
          i18nT('posV2.tableDiscountAppliedToast')
        );
      } else {
        await DiscountsService.removeTableSessionDiscount(session.id);
        ui.showToast(
          AlertType.Success,
          i18nT('posV2.tableDiscountRemovedToast')
        );
      }
    } catch (e) {
      // Server 400 messages (POS-blocked, over-room max) surface through it.
      ui.serverError(e, i18nT('posV2.tableDiscountError'));
    } finally {
      saving.value = false;
    }
  }

  return {
    state,
    openSession,
    saving,
    openModal,
  };
}
